import { useMemo } from 'react'
import { Button } from '@/common/ui/Form/Button'
import { cn } from '@/common/lib/cn/cn'
import { useAddTokensForm } from '../model'
import { TokenAmount } from '@/common/ui/TokenAmount'

type AddTokensFormProps = {
    onCancel?: () => void
    onSuccess?: () => void
    className?: string
}

export const AddTokensForm = ({ onCancel, onSuccess, className }: AddTokensFormProps) => {
    const {
        form,
        packageOptions,
        widgetError,
        isPaying,
        isWidgetReady,
        selectedPackageId,
        handlePackageSelect,
        handleSubmit,
    } = useAddTokensForm({ onSuccess })
    const packageError = form.formState.errors.packageId?.message
    const resolvedError = useMemo(() => widgetError || packageError, [packageError, widgetError])

    return (
        <form className={cn('flex flex-col gap-4 w-full', className)} onSubmit={handleSubmit}>
            <div className='grid grid-cols-1 gap-2 md:grid-cols-3 w-full'>
                {packageOptions.map(pkg => {
                    const isActive = selectedPackageId === pkg.id
                    const packageField = form.register('packageId', {
                        valueAsNumber: true,
                    })
                    return (
                        <label
                            key={pkg.id}
                            className={cn(
                                'flex flex-col items-center justify-center gap-4 py-[1.5rem] px-8 rounded-[12px] bg-[rgba(255,255,255,0.02)] transition-colors cursor-pointer',
                                { 'ring-1 ring-[#be96ff] bg-[rgba(255,255,255,0.05)]': isActive }
                            )}
                            aria-label={`Пакет ${pkg.name} на ${pkg.tokens} токенов`}
                            tabIndex={0}
                            onKeyDown={event => {
                                if (event.key !== 'Enter' && event.key !== ' ') return
                                event.preventDefault()
                                handlePackageSelect(pkg.id)
                            }}
                        >
                            <input
                                {...packageField}
                                type='radio'
                                value={pkg.id}
                                checked={isActive}
                                onChange={event => {
                                    packageField.onChange(event)
                                    handlePackageSelect(pkg.id)
                                }}
                                className='sr-only'
                                aria-checked={isActive}
                            />
                            <TokenAmount value={pkg.tokens} />
                            <div className='text-2xl font-semibold text-white leading-[1.4]'>
                                {pkg.price.toLocaleString('ru-RU')} ₽
                            </div>
                            <div className='text-xs text-[var(--pixorion-approval)] leading-[1.3] font-medium'>
                                {pkg.name}
                            </div>
                        </label>
                    )
                })}
            </div>
            {resolvedError && (
                <p className='text-sm text-[#ff8a8a]' role='alert'>
                    {resolvedError}
                </p>
            )}
            <div className='flex items-center justify-end gap-2 w-full'>
                {onCancel && (
                    <Button
                        size='lg'
                        emphasis='accent'
                        variant='ghost'
                        className='px-4'
                        onClick={onCancel}
                        type='button'
                    >
                        Отмена
                    </Button>
                )}
                <Button
                    size='lg'
                    emphasis='accent'
                    className={cn('px-6', {
                        'text-white': selectedPackageId && isWidgetReady && !isPaying,
                        'text-[#aba7af]': !selectedPackageId || !isWidgetReady || isPaying,
                    })}
                    disabled={!selectedPackageId || !isWidgetReady || isPaying}
                    loading={isPaying}
                    type='submit'
                >
                    Оплатить
                </Button>
            </div>
        </form>
    )
}
